import { buildStepContent } from '../block-builder';
import { insertHost, keepHostAttached } from '../inline-host';
import type { GuideInteractionMetadata, GuideRenderContext, GuideStep } from '../types';
import { waitForElement } from '../wait-for-element';
import { buildWalkthroughStepContent } from '../walkthrough-block-builder';
import { BaseRenderer } from './base-renderer';

/**
 * Renderiza una guía tipo `banner`. Dos modos:
 *
 *  - **Pantalla completa** (default): barra fija arriba o abajo del viewport.
 *    La posición se lee de `step.style.position` (`top` | `bottom`).
 *  - **Embebido en un contenedor** (`step.selector` presente): el banner se
 *    inserta DENTRO del contenedor elegido (prepend con `top`, append con
 *    `bottom`) y empuja su contenido — como los "element banners" de Pendo.
 *    Reusa el watchdog inline (`keepHostAttached`) porque los frameworks del
 *    host borran nodos ajenos al re-renderizar.
 *
 * No tiene backdrop ni atrapa el foco. Con `context.nav` (paso de un
 * walkthrough heterogéneo) muestra botones de navegación y delega
 * tracking/avance al controller — solo en modo pantalla completa.
 */
export class BannerRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const idx = context.nav?.stepIndex ?? 0;
    const step = context.guide.guideSteps[idx];
    if (!step) return;

    const position = step.style?.position === 'bottom' ? 'bottom' : 'top';
    // Con selector el banner se EMBEBE en ese contenedor — también en pasos de
    // secuencia (el chrome de navegación viaja adentro). En un paso-tooltip el
    // selector es el ancla; en un paso-banner es el contenedor: no colisionan
    // porque cada paso tiene UNA presentación.
    const selector = step.selector?.trim() || null;

    let anchor: Element | null = null;
    if (selector) {
      anchor = await waitForElement(selector);
      if (!anchor) return; // contenedor ausente → no pintar (como tooltip/inline)
    }

    const { host, root } = this.createHost();
    this.applyDesign(step.style);
    const ownerDocument = root.ownerDocument ?? document;

    const banner = ownerDocument.createElement('div');
    banner.className = bannerClassOf(position, Boolean(anchor));

    const dismiss = (
      action: 'dismissed' | 'cta_clicked',
      ctaUrl?: string,
      meta?: GuideInteractionMetadata,
    ) => {
      context.onInteraction({
        guideId: context.guide.guideId,
        stepIndex: idx,
        action,
        ...(meta ? { metadata: meta } : {}),
      });
      if (ctaUrl) window.open(ctaUrl, '_blank', 'noopener,noreferrer');
      context.onClose();
    };

    const content = context.nav
      ? buildWalkthroughStepContent(
          step,
          context.nav.stepIndex,
          context.nav.totalSteps,
          ownerDocument,
          context.nav.callbacks,
        )
      : buildStepContent(step, ownerDocument, {
          onCtaClick: (action, target, meta) => {
            dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
            if (action === 'guide' && target) context.onOpenGuide?.(target);
          },
          onDismiss: () => dismiss('dismissed'),
        });

    banner.appendChild(content);
    root.appendChild(banner);

    if (selector && anchor) {
      host.style.display = 'block';
      const insertAt = position === 'top' ? 'prepend' : 'append';
      insertHost(anchor, host, insertAt);
      this.registerCleanup(keepHostAttached(host, selector, insertAt));
    }

    if (!context.nav) {
      this.liveContainer = banner;
      this.liveContent = content;
      this.liveBuild = (s) =>
        buildStepContent(s, ownerDocument, {
          onCtaClick: (action, target, meta) => {
            dismiss('cta_clicked', action === 'url' ? target : undefined, meta);
            if (action === 'guide' && target) context.onOpenGuide?.(target);
          },
          onDismiss: () => dismiss('dismissed'),
        });
      this.liveKey = this.liveKeyOf(step);
      context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
    }
  }

  /** Cambiar de contenedor o de top/bottom embebido requiere re-insertar → remontar. */
  protected override liveKeyOf(step: GuideStep): string {
    const selector = step.selector?.trim() ?? '';
    if (!selector) return '';
    const position = step.style?.position === 'bottom' ? 'bottom' : 'top';
    return `${selector}|${position}`;
  }

  protected override onLiveUpdate(step: GuideStep): void {
    // La posición del banner de pantalla completa (top/bottom) es solo una
    // clase del wrapper — se actualiza en vivo. (El embebido remonta vía liveKey.)
    if (this.liveContainer) {
      const position = step.style?.position === 'bottom' ? 'bottom' : 'top';
      const embedded = Boolean(step.selector?.trim());
      this.liveContainer.className = bannerClassOf(position, embedded);
    }
  }
}

function bannerClassOf(position: 'top' | 'bottom', embedded: boolean): string {
  return embedded ? 'veo-banner veo-banner-embedded' : `veo-banner veo-banner-${position}`;
}
